import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  IconButton,
  Tooltip
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { snowflakeApi } from '../../utils/api';
import { useToast } from '../../context/ToastContext';

interface KeywordData {
  keyword: string;
  impression?: number;
  source?: string;
}

interface KeywordPreviewPanelProps {
  clientId: string;
  workflowId: string;
  limit?: number;
}

const KeywordPreviewPanel: React.FC<KeywordPreviewPanelProps> = ({ 
  clientId, 
  workflowId, 
  limit = 1000 
}) => {
  const theme = useTheme();
  const { showToast } = useToast();
  
  const [keywords, setKeywords] = useState<KeywordData[]>([]);
  const [keywordCount, setKeywordCount] = useState<number>(0);
  const [totalKeywordCount, setTotalKeywordCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (clientId && workflowId) {
      loadKeywords();
    }
  }, [clientId, workflowId, limit]);

  const loadKeywords = async () => {
    setIsLoading(true);
    try {
      // First get the total count
      const countQuery = `
        SELECT COUNT(*) as total_count
        FROM TOTALSERP.DISTINCT_LIST
        WHERE CLIENT_ID = '${clientId}'
          AND WORKFLOW_ID = '${workflowId}'
      `;
      
      const countResponse = await snowflakeApi.post('/api/v1/snowflake/query', {
        query: countQuery
      });
      
      const total = countResponse.data?.data?.[0]?.TOTAL_COUNT || countResponse.data?.data?.[0]?.total_count || 0;
      setTotalKeywordCount(total);
      
      // Then get the preview keywords
      const query = `
        SELECT 
          KEYWORD,
          IMPRESSION,
          SOURCE
        FROM TOTALSERP.DISTINCT_LIST
        WHERE CLIENT_ID = '${clientId}'
          AND WORKFLOW_ID = '${workflowId}'
        ORDER BY IMPRESSION DESC NULLS LAST
        LIMIT ${limit}
      `;
      
      const response = await snowflakeApi.post('/api/v1/snowflake/query', {
        query: query
      });
      
      if (response.data?.data) {
        const keywordData = response.data.data.map((item: any) => ({
          keyword: item.KEYWORD || item.keyword,
          impression: item.IMPRESSION || item.impression,
          source: item.SOURCE || item.source
        }));
        
        setKeywords(keywordData);
        setKeywordCount(keywordData.length);
      } else {
        throw new Error('Failed to load queries');
      }
    } catch (error) {
      console.error('Error loading queries:', error);
      showToast('Failed to load queries preview', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', height: '100%', display: 'flex', justifyContent: 'flex-end' }}>
      <Paper
        elevation={2}
        sx={{
          p: isCollapsed ? 0 : 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          height: '100%',
          width: isCollapsed ? '50px' : '360px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {/* Toggle Button */}
        <Tooltip title={isCollapsed ? 'Show Queries' : 'Hide Queries'} placement="left">
          <IconButton
            onClick={() => setIsCollapsed(!isCollapsed)}
            sx={{
              position: 'absolute',
              left: isCollapsed ? 8 : 'auto',
              right: isCollapsed ? 'auto' : 8,
              top: 8,
              zIndex: 10,
            }}
          >
            {isCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Tooltip>

        {!isCollapsed && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Preview Queries {keywordCount > 0 && totalKeywordCount > 0 && `(${keywordCount.toLocaleString()} / ${totalKeywordCount.toLocaleString()})`}
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            {isLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  Loading queries preview...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                <List dense>
                  {keywords.map((kw, index) => (
                    <ListItem key={index} divider>
                      <ListItemText primary={kw.keyword} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default KeywordPreviewPanel;

